import {
  type Item,
  PendingAsyncActions,
  type SyncItem,
  type SyncItemWithRevisions,
  type SyncResponseDataMap,
  SyncResponseMessageType,
  type SyncRevision,
} from "@taskfortress/core"
import { useCallback, useEffect, useReducer, useState } from "react"
import type { Dispatch } from "react"
import { createLogger } from "../../lib/logger"
import type { DataEngine } from "./DataEngine"

// Create a logger instance for hooks
const logger = createLogger("data-hooks")

// Sync status enum
export enum SyncStatus {
  IDLE = "idle",
  SYNCING = "syncing",
  COMPLETE = "complete",
  ERROR = "error",
}

// Sync state and actions
export interface SyncState {
  itemsSynced: number
  syncProgress: number
  syncStatus: SyncStatus
  syncCompletionPromise?: Promise<void>
  syncCompletionResolver?: () => void
  syncCompletionRejector?: (reason?: any) => void
}

export type SyncAction =
  | { type: "INCREMENT_SYNCED_ITEMS"; count: number; total: number }
  | { type: "SET_STATUS"; status: SyncStatus }

export const syncReducer = (
  state: SyncState,
  action: SyncAction,
): SyncState => {
  switch (action.type) {
    case "INCREMENT_SYNCED_ITEMS": {
      const newItemsSynced = state.itemsSynced + action.count
      const progress = action.total > 0 ? newItemsSynced / action.total : 0
      return {
        ...state,
        itemsSynced: newItemsSynced,
        syncProgress: progress,
      }
    }
    case "SET_STATUS": {
      let newState: SyncState = { ...state, syncStatus: action.status }

      // Resolve or reject existing promise if status changes from SYNCING
      if (state.syncStatus === SyncStatus.SYNCING) {
        if (
          action.status === SyncStatus.COMPLETE &&
          state.syncCompletionResolver
        ) {
          state.syncCompletionResolver()
        } else if (
          action.status === SyncStatus.ERROR &&
          state.syncCompletionRejector
        ) {
          state.syncCompletionRejector(new Error("Sync failed"))
        }
      }

      // Reset promise-related fields unless starting a new sync
      newState.syncCompletionPromise = undefined
      newState.syncCompletionResolver = undefined
      newState.syncCompletionRejector = undefined

      // If starting a new sync, create a new promise
      if (action.status === SyncStatus.SYNCING) {
        let resolver: () => void
        let rejector: (reason?: any) => void
        newState.syncCompletionPromise = new Promise<void>((res, rej) => {
          resolver = res
          rejector = rej
        })
        newState.syncCompletionResolver = resolver!
        newState.syncCompletionRejector = rejector!

        // Reset sync progress when starting new sync
        newState.syncProgress = 0
        newState.itemsSynced = 0
      } else if (action.status === SyncStatus.COMPLETE) {
        newState.syncProgress = 1
        newState.itemsSynced = state.itemsSynced // Keep items synced count on completion
      } else {
        // Reset progress for IDLE or ERROR
        newState.syncProgress = 0
        newState.itemsSynced = 0
      }

      return newState
    }
    default:
      return state
  }
}

// Helper function to convert SyncItem to Item
export const convertSyncItemToItem = (
  syncItem: SyncItem,
  syncRevisions: SyncRevision[] = [],
): Item => {
  return {
    id: syncItem.id,
    type: syncItem.type,
    createdAt: new Date(syncItem.createdAt as Date | number).toISOString(),
    updatedAt: new Date(syncItem.updatedAt as Date | number).toISOString(),
    revisions: syncRevisions.map((syncRev) => ({
      id: syncRev.id,
      itemId: syncRev.itemId,
      encryptedData: syncRev.encryptedData,
      createdAt: new Date(syncRev.createdAt as Date | number).toISOString(),
    })),
  }
}

// Helper function to convert SyncItemWithRevisions to Item
export const convertSyncItemWithRevisionsToItem = (
  syncItem: SyncItemWithRevisions,
): Item => {
  return convertSyncItemToItem(syncItem, syncItem.revisions)
}

export const useSyncStatus = () => {
  const [syncState, dispatchSync] = useReducer(syncReducer, {
    itemsSynced: 0,
    syncProgress: 0,
    syncStatus: SyncStatus.IDLE,
    syncCompletionPromise: undefined,
    syncCompletionResolver: undefined,
    syncCompletionRejector: undefined,
  })

  // Use PendingAsyncActions tied to the syncCompletionPromise from state
  const [pendingActions, setPendingActions] = useState<PendingAsyncActions>(
    () => new PendingAsyncActions(Promise.resolve()),
  )

  useEffect(() => {
    if (syncState.syncCompletionPromise) {
      setPendingActions(
        new PendingAsyncActions(syncState.syncCompletionPromise),
      )
    } else {
      // If there's no sync promise (IDLE, COMPLETE, ERROR), ensure
      // we use a default resolved promise for any subsequent actions.
      setPendingActions(new PendingAsyncActions(Promise.resolve()))
    }
    // We only depend on the promise itself changing, not the pendingActions instance.
  }, [syncState.syncCompletionPromise])

  return {
    syncState,
    dispatchSync,
    pendingActions,
  }
}

export interface UseSocketMessageHandlerProps {
  dispatchSync: Dispatch<SyncAction>
  dataEngine: DataEngine
  fetchItemsBatch: (timestamp: number, offset: number, limit: number) => void
}

export const useSocketMessageHandler = ({
  dispatchSync,
  dataEngine,
  fetchItemsBatch,
}: UseSocketMessageHandlerProps) => {
  const handleSocketMessage = useCallback(
    async (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data as string)
        const { type, data } = message as {
          type: SyncResponseMessageType
          data: any
        }

        switch (type) {
          case SyncResponseMessageType.UPDATED_ITEMS_COUNT: {
            const { count, timestamp } =
              data as SyncResponseDataMap[SyncResponseMessageType.UPDATED_ITEMS_COUNT]["data"]
            logger.info("Received item count for sync", { count, timestamp })

            if (count > 0) {
              // Start fetching items in batches
              fetchItemsBatch(timestamp, 0, 100)
            } else {
              // No items to sync
              logger.info("No items to sync")
              dispatchSync({
                type: "SET_STATUS",
                status: SyncStatus.COMPLETE,
              })
            }
            break
          }

          case SyncResponseMessageType.ITEMS_WITH_REVISIONS: {
            const {
              items: syncItems,
              timestamp,
              offset,
              limit,
              total,
            } = data as SyncResponseDataMap[SyncResponseMessageType.ITEMS_WITH_REVISIONS]["data"]

            // Update offline data with new items - convert from SyncItemWithRevisions to Item
            if (syncItems && syncItems.length > 0) {
              logger.info("Received items batch", {
                count: syncItems.length,
                offset,
                total,
              })

              // Convert SyncItemWithRevisions[] to Item[]
              const items = syncItems.map((syncItem) =>
                convertSyncItemWithRevisionsToItem(syncItem),
              )

              await dataEngine.updateItemsFromServer(items)

              // Update sync count and progress using the reducer
              dispatchSync({
                type: "INCREMENT_SYNCED_ITEMS",
                count: syncItems.length,
                total,
              })

              // If there are more items, request the next batch
              if (offset + limit < total) {
                fetchItemsBatch(timestamp, offset + limit, limit)
              } else {
                logger.info("Sync completed successfully")
                // Update last sync timestamp only after all items are synced
                const currentTime = Date.now()
                await dataEngine.setLastSyncTimestamp(currentTime)

                // Set the sync status to complete
                dispatchSync({
                  type: "SET_STATUS",
                  status: SyncStatus.COMPLETE,
                })
              }
            } else {
              // No items returned, sync is complete
              logger.info("No more items to sync, completing sync")
              // Update last sync timestamp when sync is complete with no items
              const currentTime = Date.now()
              await dataEngine.setLastSyncTimestamp(currentTime)
              dispatchSync({
                type: "SET_STATUS",
                status: SyncStatus.COMPLETE,
              })
            }
            break
          }

          case SyncResponseMessageType.ITEMS_UPDATED: {
            const { updates } =
              data as SyncResponseDataMap[SyncResponseMessageType.ITEMS_UPDATED]["data"]

            logger.info("Received items updated notification", {
              count: updates.length,
              itemIds: updates.map((u) => u.item.id),
            })

            // Process all received updates
            for (const update of updates) {
              const { item, revision } = update

              // Convert SyncItem to Item
              const updatedItem = convertSyncItemToItem(item)

              // Add the revision to the item
              updatedItem.revisions = [
                {
                  id: revision.id,
                  itemId: revision.itemId,
                  encryptedData: revision.encryptedData,
                  createdAt:
                    typeof revision.createdAt === "number"
                      ? new Date(revision.createdAt).toISOString()
                      : revision.createdAt
                        ? revision.createdAt.toISOString()
                        : new Date().toISOString(),
                },
              ]

              // Update the local storage with the new items
              await dataEngine.updateItemsFromServer([updatedItem])
            }
            break
          }

          case SyncResponseMessageType.UPDATE_ITEMS_SUCCESS: {
            // Acknowledge successful items update
            const { itemIds } =
              data as SyncResponseDataMap[SyncResponseMessageType.UPDATE_ITEMS_SUCCESS]["data"]
            logger.info("Server acknowledged items update", {
              itemIds,
              count: itemIds.length,
            })
            break
          }
        }
      } catch (error) {
        logger.error("Error handling socket message", { error })
      }
    },
    [dataEngine, dispatchSync, fetchItemsBatch],
  )

  return { handleSocketMessage }
}
